import React, { useState, useRef } from 'react';
import { useTrackWidget } from 'shared/util/analytics/useTrackWidget';
import { track } from 'shared/util/analytics/track';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { Config, SearchResult } from './types';
import { IconX } from '@tabler/icons-react';
import { PaddingData } from 'builder/components/WidgetConfigurations/Padding';
import { truncateDescription, highlightKeyword } from './Search.util';

const SearchContainer = styled.div`
  position: relative;
  max-width: 600px;
  margin: 20px auto;
  width: 100%;
`;

const SearchInput = styled.input<{
  borderColor: string;
  background: string;
  textColor: string;
  borderData: BorderData;
  paddingData: PaddingData;
  font: string;
}>`
  width: 100%;
  padding: 12px 40px;
  border-radius: ${props => props.borderData.radius}px;
  border-top: ${props =>
    props.borderData?.isTopBorderVisible ?? true
      ? `${props.borderData.thickness}px solid ${props.borderColor}`
      : 'none'};
  border-bottom: ${props =>
    props.borderData?.isBottomBorderVisible ?? true
      ? `${props.borderData.thickness}px solid ${props.borderColor}`
      : 'none'};
  border-left: ${props =>
    props.borderData?.isLeftBorderVisible ?? true
      ? `${props.borderData.thickness}px solid ${props.borderColor}`
      : 'none'};
  border-right: ${props =>
    props.borderData?.isRightBorderVisible ?? true
      ? `${props.borderData.thickness}px solid ${props.borderColor}`
      : 'none'};
  background: ${props => props.background || '#222'};
  color: ${props => props.textColor || '#fff'};
  padding-top: ${props => props.paddingData?.top}px;
  padding-right: ${props => props.paddingData?.right}px;
  padding-bottom: ${props => props.paddingData?.bottom}px;
  padding-left: ${props => props.paddingData?.left}px;
  font-size: 14px;
  outline: none;
  transition: all 0.3s ease-in-out;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  font-family: ${props => props.font || 'sans-serif'};
  &::placeholder {
    color: #aaa;
  }

  &:focus {
    border-color: #1e90ff;
    box-shadow: 0 0 10px rgba(30, 144, 255, 0.6);
  }
`;

const SearchResultsContainer = styled.div<{
  background: string;
  borderColor: string;
  font: string;
}>`
  position: absolute;
  width: 100%;
  background: ${props => props.background || '#222'};
  border-radius: 8px;
  margin-top: 5px;
  padding: 10px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.4);
  max-height: 390px;
  overflow-y: auto;
  z-index: 1000;
  border: 1px solid ${props => props.borderColor || '#444'};
  font-family: ${props => props.font || 'sans-serif'};
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 10px;
  background: transparent;
  border: none;
  font-size: 20px;
  color: #bbb;
  cursor: pointer;

  &:hover {
    color: #fff;
  }
`;

const ResultItem = styled.a<{ textColor: string; hoverColor: string }>`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: ${props => props.textColor || '#ddd'};
  padding: 12px;
  border-bottom: 1px solid #444;
  transition: background 0.2s ease-in-out;

  &:hover {
    background-color: ${props => props.hoverColor || '#333'};
  }
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: bold;
  margin: 5px 0;
  color: #1e90ff;

  &:hover {
    text-decoration: underline;
  }
`;

const Description = styled.div`
  font-size: 14px;
  color: #ccc;
  /* Clamp inline dropdown snippets to 2 lines */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const SeeAllButton = styled.div`
  padding: 12px;
  text-align: center;
  cursor: pointer;
  color: #1e90ff;
  font-weight: bold;
  transition: background 0.2s ease-in-out;

  &:hover {
    background-color: #333;
  }
`;

const Loader = styled.div`
  text-align: center;
  padding: 20px;
  color: #fff;
`;

interface SearchBarWidgetProps extends WidgetRenderProps<Config> {
  fetchSearchResults: (query: string, site: Site) => Promise<SearchResult[]>;
  site: Site;
}

const SearchBarWidget: React.FC<SearchBarWidgetProps> = ({
  fetchSearchResults,
  widgetState,
  site,
  currentPage,
}) => {
  // Guard against undefined widgetState in isolated tests by providing safe defaults.
  const {
    borderData,
    backgroundData,
    lineColor,
    textColor,
    paddingData,
    font,
  } = widgetState?.config ?? {
    borderData: { thickness: 0, radius: 0 },
    backgroundData: { style: 'Color', color: 'transparent' },
    lineColor: '#000',
    textColor: '#000',
    paddingData: { top: 0, right: 0, bottom: 0, left: 0 },
    font: 'OptumSans',
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [filteredResults, setFilteredResults] = useState<SearchResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const trackSearch = useTrackWidget({
    tracking: (widgetState as any)?.tracking,
    widgetState: (widgetState as any) ?? {},
    currentPage: currentPage ?? (widgetState as any)?.currentPage,
    site: site,
  });

  const isEditing = Boolean((widgetState as any)?.editing);
  const analyticsLabel =
    (widgetState as any)?.config?.analyticsLabel || 'SearchWidget';

  const handleInputChange = (e?: React.ChangeEvent<HTMLInputElement>) => {
    const query = e ? e.target.value : '';
    setSearchQuery(query);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      if (query.trim() === '') {
        setFilteredResults([]);
      } else {
        setLoading(true);
        if (!isEditing && query.trim().length > 0) {
          trackSearch('search_widget_query_change', {
            query: query.trim(),
            widgetAnalyticsLabel: analyticsLabel,
          });
        }
        fetchSearchResults(query, site)
          .then(results => {
            setFilteredResults(results);
            setShowResults(true);
            setLoading(false);
            if (!isEditing && query.trim().length > 0) {
              trackSearch('search_widget_results_loaded', {
                query: query.trim(),
                resultsCount: results.length,
                widgetAnalyticsLabel: analyticsLabel,
              });
            }
          })
          .catch((error: Error) => {
            setLoading(false);
            setFilteredResults([]);
            setShowResults(true);
            console.error('Search API error:', error);
          });
      }
    }, 300);
  };

  const handleCloseResults = () => {
    setShowResults(false);
    if (!isEditing) {
      trackSearch('search_widget_results_closed', {
        query: searchQuery.trim(),
        widgetAnalyticsLabel: analyticsLabel,
      });
    }
  };

  const handleSeeAll = () => {
    const pageId = currentPage?.pageId?.toString();

    const queryParams = new URLSearchParams();
    if (searchQuery.trim()) queryParams.set('q', searchQuery.trim());
    if (pageId) queryParams.set('pageId', pageId);

    if (!isEditing) {
      trackSearch('search_widget_see_all_click', {
        query: searchQuery.trim(),
        widgetAnalyticsLabel: analyticsLabel,
      });
    }

    history.push(
      `/sites/${site.siteId}/search-results?${queryParams.toString()}`
    );
    setShowResults(false);
  };

  const renderResults = () => {
    if (loading) {
      return <Loader>Loading...</Loader>;
    }

    if (filteredResults.length > 0) {
      return filteredResults.map(result => (
        <ResultItem
          key={result.link || result.title}
          href={result.link}
          textColor={'#fff'}
          hoverColor={'#333'}
          onClick={() => {
            if (!isEditing) {
              trackSearch('search_widget_result_click', {
                query: searchQuery.trim(),
                resultTitle: result.title,
                resultCategory: result.category,
                widgetAnalyticsLabel: analyticsLabel,
              });
            }
          }}
        >
          <Title>{highlightKeyword(result.title, searchQuery)}</Title>
          <Description>
            {highlightKeyword(
              truncateDescription(result.description, 200),
              searchQuery
            )}
          </Description>
        </ResultItem>
      ));
    }

    return (
      <Description
        data-test-id="no-results-found"
        style={{
          textAlign: 'center',
          padding: '10px',
          color: textColor || '#fff',
        }}
      >
        No results found
      </Description>
    );
  };

  return (
    <SearchContainer>
      <SearchInput
        data-test-id="search-input"
        type="text"
        placeholder="Search or jump to..."
        value={searchQuery}
        onChange={handleInputChange}
        borderColor={lineColor}
        background={
          backgroundData?.style === 'Color' ? backgroundData.color : '#222'
        }
        textColor={textColor || '#fff'} // Ensure white text color in dark mode
        borderData={borderData}
        paddingData={paddingData}
        font={font}
      />

      {showResults && (
        <SearchResultsContainer
          data-test-id="search-results-container"
          background={
            backgroundData?.style === 'Color' ? backgroundData.color : '#222'
          }
          borderColor={lineColor}
          font={font}
        >
          <CloseButton data-test-id="close-button" onClick={handleCloseResults}>
            <IconX />
          </CloseButton>
          {renderResults()}
          {filteredResults.length > 0 && (
            <SeeAllButton
              data-test-id="see-all-results"
              onMouseDown={e => e.preventDefault()}
              onClick={handleSeeAll}
            >
              See all results ({filteredResults.length})
            </SeeAllButton>
          )}
        </SearchResultsContainer>
      )}
    </SearchContainer>
  );
};

export default track()(SearchBarWidget);
